import React, { useState } from "react";
import {
  Select,
  TimePicker,
  DatePicker,
  InputNumber,
  Form,
  Button,
  Popover,
  Col,
  Row,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useWatch } from "antd/es/form/Form";

const { Option } = Select;
const { Text } = Typography;

const reminderTypeOptions = ["Daily", "On", "Before", "After"];
const offsetFromOptions = [
  "Specific Date",
  "Start Date",
  "Created Date",
  "Due Date",
  "Completion Date",
];

interface User {
  label: string;
  value: number;
}

interface Reminder {
  reminderType: string;
  offsetFrom?: string;
  specificDate?: string;
  specificTime?: string;
  daysOffset?: number;
  reminderUsers?: { userId: number }[];
}

interface ReminderPopperProps {
  setRemainders: (value: any) => void;
  remainders?: any; // Add current remainders state
  users?: any[];
  disabled?: boolean;
  selectedTaskDetails?: {
    reminders?: Reminder;
    taskPlannedStartDate?: string;
  };
  isSubtaskUpdate?: boolean;
}

const ReminderPopper = ({
  setRemainders,
  remainders,
  users,
  disabled,
  selectedTaskDetails,
  isSubtaskUpdate,
}: ReminderPopperProps) => {
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [savedReminderType, setSavedReminderType] = useState<string | null>(
    null
  );
  const [displayText, setDisplayText] = useState<string>("");

  const reminderTypeWatched = useWatch("reminderType", form);
  const offsetFromWatched = useWatch("offsetFrom", form);
  const specificTimeWatched = useWatch("specificTime", form);
  const specificDateWatched = useWatch("specificDate", form);

  // Reset 'specificDate' if offsetFromWatched is not 'Specific Date'
  React.useEffect(() => {
    if (offsetFromWatched != "Specific Date") {
      form.setFieldsValue({ specificDate: null });
      form.resetFields(["specificDate"]);
    }
  }, [offsetFromWatched, form]);

  // Force re-render when form values change
  React.useEffect(() => {
    // Update display text when form values change
    const newDisplayText = getReminderText();
    setDisplayText(newDisplayText);
  }, [
    reminderTypeWatched,
    offsetFromWatched,
    specificTimeWatched,
    specificDateWatched,
  ]);

  // Update display text when remainders state changes
  React.useEffect(() => {
    const newDisplayText = getReminderText();
    setDisplayText(newDisplayText);
  }, [remainders]);

  React.useEffect(() => {
    if (selectedTaskDetails?.reminders) {
      const uniqueReminderUsers = Array.from(
        new Set(
          selectedTaskDetails.reminders.reminderUsers
            ?.map((user) => user.userId)
            .filter((userId) => users?.some((u) => u.value === userId))
        )
      );
      const reminderType =
        selectedTaskDetails.reminders.reminderType || "Daily";
      form.setFieldsValue({
        reminderType: reminderType,
        offsetFrom:
          reminderType === "Daily"
            ? undefined
            : selectedTaskDetails.reminders.offsetFrom,
        specificDate:
          reminderType === "Daily"
            ? null
            : selectedTaskDetails.reminders.specificDate
            ? dayjs(selectedTaskDetails.reminders.specificDate)
            : null,
        daysOffset:
          reminderType === "Daily"
            ? undefined
            : selectedTaskDetails.reminders.daysOffset,
        specificTime: selectedTaskDetails.reminders.specificTime
          ? dayjs(selectedTaskDetails.reminders.specificTime, "HH:mm")
          : null,
        reminderUsers: uniqueReminderUsers,
      });
      setSavedReminderType(
        selectedTaskDetails.reminders.reminderType || "Daily"
      );

      // Initialize display text
      setTimeout(() => {
        const initialText = getReminderText();
        setDisplayText(initialText);
      }, 100);
    }
  }, [selectedTaskDetails, form, users]);

  const handleVisibleChange = (open: boolean) => {
    if (!disabled && !isSubtaskUpdate) {
      // Prevent opening if isSubtaskUpdate is true
      setVisible(open);
      if (open) {
        // Use the current selectedTaskDetails reminder type if available, otherwise fall back to savedReminderType
        const currentReminderType =
          selectedTaskDetails?.reminders?.reminderType ||
          savedReminderType ||
          "Daily";
        form.setFieldsValue({ reminderType: currentReminderType });
      }
    }
  };

  const handleSelectClick = () => {
    if (!disabled && !isSubtaskUpdate) {
      // Prevent opening if isSubtaskUpdate is true
      setVisible(true);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const uniqueReminderUsers = Array.from(new Set(values.reminderUsers));
      const formatted = {
        ...values,
        specificDate: values.specificDate
          ? dayjs(values.specificDate).format("YYYY-MM-DD")
          : undefined,
        specificTime: values.specificTime
          ? dayjs(values.specificTime).format("HH:mm")
          : undefined,
        reminderUsers: uniqueReminderUsers.map((id: any) => ({
          userId: id,
        })),
        moduleType: "Task",
        isLoadedFromAPI: false,
      };

      // For "On" type reminders, only set offsetFrom if user selected "Specific Date"
      // Otherwise, let the user's selection remain
      if (
        values.reminderType === "On" &&
        values.offsetFrom === "Specific Date"
      ) {
        formatted.offsetFrom = "Specific Date";
      }

      // For "On" type with non-"Specific Date" offset, calculate the specific date
      if (
        values.reminderType === "On" &&
        values.offsetFrom !== "Specific Date" &&
        values.daysOffset &&
        selectedTaskDetails?.taskPlannedStartDate
      ) {
        // Calculate specific date based on task start date and days offset
        const taskStartDate = dayjs(selectedTaskDetails.taskPlannedStartDate);
        const calculatedDate = taskStartDate.add(values.daysOffset, "day");
        formatted.specificDate = calculatedDate.format("YYYY-MM-DD");
      }

      // Remove offsetFrom and related fields for Daily reminder type
      if (values.reminderType === "Daily") {
        delete formatted.offsetFrom;
        delete formatted.specificDate;
        delete formatted.daysOffset;
      }

      setRemainders((prev: any) => {
        // For Daily reminder type, completely replace the state to avoid old data
        if (values.reminderType === "Daily") {
          return formatted;
        }

        // For other reminder types, merge with previous state but ensure proper cleanup
        const merged =
          prev && Object.keys(prev).length
            ? { ...prev, ...formatted }
            : { ...formatted };

        // Ensure we don't carry over old fields that shouldn't be there
        if (values.reminderType !== "On") {
          delete merged.specificDate;
        }
        if (
          values.reminderType !== "Before" &&
          values.reminderType !== "After" &&
          values.reminderType !== "On"
        ) {
          delete merged.daysOffset;
          delete merged.offsetFrom;
        }

        return merged;
      });

      setSavedReminderType(values.reminderType);
      setVisible(false);
    } catch (err) {
      // Validation failed
    }
  };

  const getReminderText = () => {
    // Priority order: current remainders state > form values > selectedTaskDetails
    const currentReminderType =
      remainders?.reminderType ||
      form.getFieldValue("reminderType") ||
      reminderTypeWatched ||
      savedReminderType;
    const currentSpecificTime =
      remainders?.specificTime ||
      form.getFieldValue("specificTime") ||
      specificTimeWatched;
    const currentSpecificDate =
      remainders?.specificDate ||
      form.getFieldValue("specificDate") ||
      specificDateWatched;
    const currentOffsetFrom =
      remainders?.offsetFrom ||
      form.getFieldValue("offsetFrom") ||
      offsetFromWatched;

    // If we have current form values, use them
    if (currentReminderType && currentSpecificTime) {
      let text = `${currentReminderType}`;

      // Only add date for "On" type reminders
      if (currentReminderType === "On" && currentSpecificDate) {
        text += ` ${dayjs(currentSpecificDate).format("MMM DD")}`;
      }
      // Only add offset for "Before", "After", or "On" type reminders with non-Specific Date offset
      else if (
        (currentReminderType === "Before" ||
          currentReminderType === "After" ||
          currentReminderType === "On") &&
        currentOffsetFrom &&
        currentOffsetFrom !== "Specific Date"
      ) {
        // Make offset text more concise
        const shortOffset = currentOffsetFrom
          .replace(" Date", "")
          .replace(" Date", "");
        text += ` ${shortOffset}`;
      }

      // Format time safely - handle both dayjs objects and strings
      let timeText = "";
      try {
        if (dayjs.isDayjs(currentSpecificTime)) {
          timeText = currentSpecificTime.format("HH:mm");
        } else if (typeof currentSpecificTime === "string") {
          timeText = dayjs(currentSpecificTime, "HH:mm").format("HH:mm");
        } else {
          timeText = dayjs(currentSpecificTime).format("HH:mm");
        }
      } catch (error) {
        console.error("Error formatting time:", error, currentSpecificTime);
        timeText = "00:00"; // fallback
      }

      text += ` ${timeText}`;
      return text;
    }

    // Fallback to existing reminder data if no form values
    if (selectedTaskDetails?.reminders) {
      const reminder = selectedTaskDetails.reminders;
      if (reminder.reminderType && reminder.specificTime) {
        let text = `${reminder.reminderType}`;

        // Only add date for "On" type reminders
        if (reminder.reminderType === "On" && reminder.specificDate) {
          text += ` ${dayjs(reminder.specificDate).format("MMM DD")}`;
        }
        // Only add offset for "Before", "After", or "On" type reminders with non-Specific Date offset
        else if (
          (reminder.reminderType === "Before" ||
            reminder.reminderType === "After" ||
            reminder.reminderType === "On") &&
          reminder.offsetFrom &&
          reminder.offsetFrom !== "Specific Date"
        ) {
          // Make offset text more concise
          const shortOffset = reminder.offsetFrom
            .replace(" Date", "")
            .replace(" Date", "");
          text += ` ${shortOffset}`;
        }

        // Format time safely for fallback
        let timeText = "";
        try {
          if (dayjs.isDayjs(reminder.specificTime)) {
            timeText = reminder.specificTime.format("HH:mm");
          } else if (typeof reminder.specificTime === "string") {
            timeText = dayjs(reminder.specificTime, "HH:mm").format("HH:mm");
          } else {
            timeText = dayjs(reminder.specificTime).format("HH:mm");
          }
        } catch (error) {
          console.error(
            "Error formatting time in fallback:",
            error,
            reminder.specificTime
          );
          timeText = "00:00"; // fallback
        }

        text += ` ${timeText}`;
        return text;
      }
    }

    return "Set Reminder";
  };

  const popoverContent = (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{ reminderType: "Daily" }}
      disabled={isSubtaskUpdate || disabled}
      style={{ width: "100%" }}
    >
      <Row gutter={12} align="bottom">
        <Col span={12}>
          <Form.Item
            name="reminderType"
            label="Reminder Type"
            rules={[{ required: true, message: "Select reminder type" }]}
          >
            <Select
              onChange={(value) => {
                setSavedReminderType(value);

                if (value === "Daily") {
                  form.setFieldsValue({
                    offsetFrom: undefined,
                    specificDate: undefined,
                    daysOffset: undefined,
                  });
                } else {
                  form.setFieldsValue({ offsetFrom: undefined });
                }
              }}
              disabled={isSubtaskUpdate || disabled}
            >
              {reminderTypeOptions.map((type) => (
                <Option key={type} value={type}>
                  {type}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        {reminderTypeWatched && reminderTypeWatched !== "Daily" && (
          <Col span={12}>
            <Form.Item
              name="offsetFrom"
              rules={[{ required: true, message: "Select offset" }]}
              style={{ marginTop: 29 }}
            >
              <Select
                placeholder="Offset From"
                disabled={isSubtaskUpdate || disabled}
              >
                {offsetFromOptions
                  .filter((opt) => {
                    if (
                      reminderTypeWatched === "Before" ||
                      reminderTypeWatched === "After"
                    ) {
                      return opt !== "Specific Date";
                    }
                    return true;
                  })
                  .map((opt) => (
                    <Option key={opt} value={opt}>
                      {opt}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
        )}
      </Row>

      {reminderTypeWatched === "On" &&
        offsetFromWatched === "Specific Date" && (
          <Form.Item
            name="specificDate"
            label="Specific Date"
            rules={[
              {
                required: offsetFromWatched === "Specific Date" ? true : false,
                message: "Select date",
              },
            ]}
          >
            <DatePicker
              style={{ width: "100%" }}
              disabled={isSubtaskUpdate || disabled}
            />
          </Form.Item>
        )}

      {(reminderTypeWatched === "Before" ||
        reminderTypeWatched === "After" ||
        (reminderTypeWatched === "On" &&
          offsetFromWatched !== "Specific Date")) && (
        <Form.Item
          name="daysOffset"
          label="Days Offset"
          rules={[{ required: true, message: "Enter days" }]}
        >
          <InputNumber
            min={1}
            style={{ width: "100%" }}
            disabled={isSubtaskUpdate || disabled}
          />
        </Form.Item>
      )}

      <Form.Item
        name="specificTime"
        label="Time"
        rules={[{ required: true, message: "Select time" }]}
      >
        <TimePicker
          format="HH:mm"
          style={{ width: "100%" }}
          disabled={isSubtaskUpdate || disabled}
        />
      </Form.Item>

      <Form.Item
        name="reminderUsers"
        label="Notify users associated with the Task"
        rules={[{ required: true, message: "Select users" }]}
      >
        <Select
          mode="multiple"
          options={
            users?.length ? users : [{ label: "No users available", value: -1 }]
          }
          maxTagCount={1}
          maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
          placeholder="Select"
          disabled={isSubtaskUpdate || disabled || !users?.length}
        />
      </Form.Item>

      <div style={{ textAlign: "right" }}>
        <Button
          size="small"
          onClick={() => setVisible(false)}
          style={{ marginRight: 8 }}
          disabled={isSubtaskUpdate || disabled}
        >
          Cancel
        </Button>
        <Button
          size="small"
          type="primary"
          htmlType="submit"
          disabled={isSubtaskUpdate || disabled}
        >
          Save
        </Button>
      </div>
    </Form>
  );

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={visible && !(disabled || isSubtaskUpdate)} // Prevent Popover from opening if disabled or isSubtaskUpdate is true
      onOpenChange={handleVisibleChange}
      placement="bottomLeft"
      arrow={false}
      autoAdjustOverflow
      align={{
        points: ["tl", "bl"],
        offset: [0, 8],
        overflow: { adjustX: 1, adjustY: 1 },
      }}
      getPopupContainer={() => document.body}
      styles={{ body: { padding: 12 } }}
    >
      <div
        style={{
          width: "100%",
          height: "38px",
          border: `1px solid #d9d9d9`,
          borderRadius: 4,
          padding: "4px 11px",
          background: disabled || isSubtaskUpdate ? "#f5f5f5" : "#fff", // Gray out if disabled or isSubtaskUpdate
          display: "flex",
          alignItems: "center",
          cursor: disabled || isSubtaskUpdate ? "not-allowed" : "pointer", // Disable cursor if disabled or isSubtaskUpdate
          opacity: disabled || isSubtaskUpdate ? 0.65 : 1, // Reduce opacity if disabled or isSubtaskUpdate
          minWidth: 0,
          overflow: "hidden",
        }}
        onClick={handleSelectClick}
      >
        <Text
          type={savedReminderType ? undefined : "secondary"}
          style={{
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: "14px",
            lineHeight: "1.2",
            minWidth: 0,
            flex: 1,
          }}
          title={displayText || getReminderText()}
        >
          {displayText || getReminderText()}
        </Text>
      </div>
    </Popover>
  );
};

export default ReminderPopper;
